"use client"

import { useState } from "react"
import { CheckCircle2, Circle, ShieldCheck, UserCheck, FileText, Lock, AlertCircle } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"

interface ChecklistItem {
  id: string
  text: string
  category: string
  subcategory: string
  explanation: string
}

const privacyRuleItems: ChecklistItem[] = [
  // Individual Rights
  {
    id: "individual-1",
    text: "Notice of Privacy Practices",
    category: "Individual Rights",
    subcategory: "Notice Requirements",
    explanation: "Provide a Notice of Privacy Practices to all patients explaining how PHI may be used and disclosed, and outlining their rights.",
  },
  {
    id: "individual-2",
    text: "Acknowledge Receipt of Notice",
    category: "Individual Rights",
    subcategory: "Notice Requirements",
    explanation: "Make good faith effort to obtain written acknowledgment from patients that they received the Notice of Privacy Practices.",
  },
  {
    id: "individual-3",
    text: "Right of Access",
    category: "Individual Rights",
    subcategory: "Access and Amendment",
    explanation: "Provide individuals with access to their PHI in a designated record set within 30 days (with one 30-day extension allowed).",
  },
  {
    id: "individual-4",
    text: "Right to Amendment",
    category: "Individual Rights",
    subcategory: "Access and Amendment",
    explanation: "Allow individuals to request amendments to their PHI and either accept the amendment or provide written denial with opportunity to submit statement of disagreement.",
  },
  {
    id: "individual-5",
    text: "Accounting of Disclosures",
    category: "Individual Rights",
    subcategory: "Access and Amendment",
    explanation: "Provide individuals with an accounting of disclosures of their PHI for the past six years (excluding treatment, payment, operations, and certain other disclosures).",
  },
  {
    id: "individual-6",
    text: "Right to Request Restrictions",
    category: "Individual Rights",
    subcategory: "Privacy Rights",
    explanation: "Allow individuals to request restrictions on uses and disclosures of PHI. Must honor requests to restrict disclosures to health plans when individual paid out of pocket in full.",
  },
  {
    id: "individual-7",
    text: "Right to Confidential Communications",
    category: "Individual Rights",
    subcategory: "Privacy Rights",
    explanation: "Accommodate reasonable requests by individuals to receive communications of PHI by alternative means or at alternative locations.",
  },

  // Uses and Disclosures
  {
    id: "use-1",
    text: "Minimum Necessary Standard",
    category: "Uses and Disclosures",
    subcategory: "General Requirements",
    explanation: "Limit uses and disclosures of PHI to the minimum necessary to accomplish the intended purpose (except for treatment purposes, disclosures to the individual, or as required by law).",
  },
  {
    id: "use-2",
    text: "Uses and Disclosures for Treatment, Payment, and Operations",
    category: "Uses and Disclosures",
    subcategory: "Permitted Uses",
    explanation: "PHI may be used and disclosed for treatment, payment, and healthcare operations without patient authorization.",
  },
  {
    id: "use-3",
    text: "Patient Authorization Required",
    category: "Uses and Disclosures",
    subcategory: "Authorizations",
    explanation: "Obtain valid authorization from patients before using or disclosing PHI for purposes other than treatment, payment, operations, or those permitted/required by law.",
  },
  {
    id: "use-4",
    text: "Authorization for Marketing",
    category: "Uses and Disclosures",
    subcategory: "Authorizations",
    explanation: "Obtain authorization before using or disclosing PHI for marketing purposes (with limited exceptions for face-to-face communications or promotional gifts of nominal value).",
  },
  {
    id: "use-5",
    text: "Prohibition on Sale of PHI",
    category: "Uses and Disclosures",
    subcategory: "Prohibited Uses",
    explanation: "Do not sell PHI without individual authorization. Authorization must state that disclosure will result in remuneration to the covered entity.",
  },
  {
    id: "use-6",
    text: "Disclosures Required by Law",
    category: "Uses and Disclosures",
    subcategory: "Required Disclosures",
    explanation: "Disclose PHI when required by law, including to the individual upon request and to HHS for compliance investigations.",
  },
  {
    id: "use-7",
    text: "Public Health Activities",
    category: "Uses and Disclosures",
    subcategory: "Permitted Disclosures",
    explanation: "May disclose PHI without authorization for public health activities (disease reporting, surveillance, product safety, etc.).",
  },
  {
    id: "use-8",
    text: "Victims of Abuse, Neglect, or Domestic Violence",
    category: "Uses and Disclosures",
    subcategory: "Permitted Disclosures",
    explanation: "May disclose PHI to government authorities when required or permitted by law regarding abuse, neglect, or domestic violence.",
  },
  {
    id: "use-9",
    text: "Judicial and Administrative Proceedings",
    category: "Uses and Disclosures",
    subcategory: "Permitted Disclosures",
    explanation: "May disclose PHI in response to court orders, subpoenas, or discovery requests with proper documentation and patient notification.",
  },
  {
    id: "use-10",
    text: "Law Enforcement Purposes",
    category: "Uses and Disclosures",
    subcategory: "Permitted Disclosures",
    explanation: "May disclose PHI to law enforcement in response to legal process, to identify/locate suspects or fugitives, about crime victims, and about deaths.",
  },
  {
    id: "use-11",
    text: "Reproductive Health Privacy Protections (April 2024)",
    category: "Uses and Disclosures",
    subcategory: "Special Protections",
    explanation: "Strengthen privacy protections for reproductive healthcare information. Restrict disclosures of PHI related to lawful reproductive health care.",
  },

  // Administrative Requirements
  {
    id: "admin-1",
    text: "Designate Privacy Officer",
    category: "Administrative Requirements",
    subcategory: "Personnel",
    explanation: "Designate a Privacy Official responsible for developing and implementing privacy policies and procedures.",
  },
  {
    id: "admin-2",
    text: "Designate Contact Person",
    category: "Administrative Requirements",
    subcategory: "Personnel",
    explanation: "Designate a contact person or office responsible for receiving complaints and providing information about privacy practices.",
  },
  {
    id: "admin-3",
    text: "Workforce Training",
    category: "Administrative Requirements",
    subcategory: "Training",
    explanation: "Train all workforce members on privacy policies and procedures, and document the training.",
  },
  {
    id: "admin-4",
    text: "Privacy Policies and Procedures",
    category: "Administrative Requirements",
    subcategory: "Policies",
    explanation: "Develop and implement written privacy policies and procedures that comply with the Privacy Rule.",
  },
  {
    id: "admin-5",
    text: "Safeguards for PHI",
    category: "Administrative Requirements",
    subcategory: "Safeguards",
    explanation: "Implement administrative, physical, and technical safeguards to protect PHI from any intentional or unintentional use or disclosure in violation of the Privacy Rule.",
  },
  {
    id: "admin-6",
    text: "Complaints Process",
    category: "Administrative Requirements",
    subcategory: "Complaints",
    explanation: "Establish a process for individuals to make complaints concerning privacy policies and procedures or compliance with the Privacy Rule.",
  },
  {
    id: "admin-7",
    text: "Sanctions for Violations",
    category: "Administrative Requirements",
    subcategory: "Enforcement",
    explanation: "Apply appropriate sanctions against workforce members who violate privacy policies or the Privacy Rule.",
  },
  {
    id: "admin-8",
    text: "Mitigation of Harmful Effects",
    category: "Administrative Requirements",
    subcategory: "Breach Response",
    explanation: "Mitigate, to the extent practicable, any harmful effect from use or disclosure of PHI in violation of policies or the Privacy Rule.",
  },
  {
    id: "admin-9",
    text: "No Retaliation/Waiver",
    category: "Administrative Requirements",
    subcategory: "Protections",
    explanation: "Do not retaliate against individuals for exercising their privacy rights or participating in compliance activities. Do not require individuals to waive their privacy rights.",
  },
  {
    id: "admin-10",
    text: "Documentation and Retention",
    category: "Administrative Requirements",
    subcategory: "Documentation",
    explanation: "Maintain written privacy policies and procedures, Notice of Privacy Practices, and other required documentation for 6 years from creation or last effective date.",
  },

  // Business Associates
  {
    id: "ba-1",
    text: "Business Associate Agreements",
    category: "Business Associates",
    subcategory: "Contracts",
    explanation: "Obtain satisfactory written assurance (Business Associate Agreement) from business associates that they will safeguard PHI.",
  },
  {
    id: "ba-2",
    text: "BAA Required Elements",
    category: "Business Associates",
    subcategory: "Contracts",
    explanation: "Ensure BAAs include required elements: permitted uses/disclosures, safeguard requirements, reporting obligations, and termination provisions.",
  },
  {
    id: "ba-3",
    text: "Subcontractor Requirements",
    category: "Business Associates",
    subcategory: "Contracts",
    explanation: "Ensure business associates obtain written assurances from their subcontractors who handle PHI.",
  },
  {
    id: "ba-4",
    text: "Due Diligence",
    category: "Business Associates",
    subcategory: "Oversight",
    explanation: "Exercise due diligence in selecting business associates and monitor their compliance with BAA terms.",
  },

  // Breach Notification
  {
    id: "breach-1",
    text: "Breach Risk Assessment",
    category: "Breach Notification",
    subcategory: "Assessment",
    explanation: "Conduct risk assessment to determine if unauthorized use or disclosure constitutes a breach requiring notification.",
  },
  {
    id: "breach-2",
    text: "Individual Notification (72 hours)",
    category: "Breach Notification",
    subcategory: "Notification Timelines",
    explanation: "Notify affected individuals within 72 hours of discovering a breach (updated from 60 days in January 2025).",
  },
  {
    id: "breach-3",
    text: "Media Notification (500+ individuals)",
    category: "Breach Notification",
    subcategory: "Notification Requirements",
    explanation: "Notify prominent media outlets if breach affects more than 500 residents of a state or jurisdiction.",
  },
  {
    id: "breach-4",
    text: "HHS Notification",
    category: "Breach Notification",
    subcategory: "Notification Requirements",
    explanation: "Notify HHS immediately for breaches affecting 500+ individuals, or annually for smaller breaches.",
  },
  {
    id: "breach-5",
    text: "Breach Notification Content",
    category: "Breach Notification",
    subcategory: "Notification Requirements",
    explanation: "Include required elements in breach notifications: description of breach, types of information involved, steps individuals should take, what entity is doing, and contact information.",
  },
  {
    id: "breach-6",
    text: "Breach Documentation",
    category: "Breach Notification",
    subcategory: "Documentation",
    explanation: "Maintain documentation of all breaches, including those not requiring notification, and the risk assessment rationale.",
  },
]

export default function PrivacyRuleChecklistContent() {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set())
  const [activeCategory, setActiveCategory] = useState<string>("Individual Rights")

  const toggleItem = (id: string) => {
    const newChecked = new Set(checkedItems)
    if (newChecked.has(id)) {
      newChecked.delete(id)
    } else {
      newChecked.add(id)
    }
    setCheckedItems(newChecked)
  }

  const categories = Array.from(new Set(privacyRuleItems.map(item => item.category)))

  const getCategoryIcon = (category: string) => {
    if (category === "Individual Rights") return UserCheck
    if (category === "Uses and Disclosures") return FileText
    if (category === "Administrative Requirements") return ShieldCheck
    if (category === "Business Associates") return FileText
    return Lock
  }

  const getCategoryProgress = (category: string) => {
    const items = privacyRuleItems.filter(item => item.category === category)
    const checked = items.filter(item => checkedItems.has(item.id)).length
    return { checked, total: items.length, percentage: Math.round((checked / items.length) * 100) }
  }

  const overallProgress = Math.round((checkedItems.size / privacyRuleItems.length) * 100)

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-white/20 text-white hover:bg-white/30 mb-6">
              <ShieldCheck className="mr-2 h-4 w-4" />
              HIPAA Privacy Rule
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Rule Compliance Checklist</h1>
            <p className="text-xl text-white/90">
              Complete checklist of HIPAA Privacy Rule requirements (45 CFR Part 164, Subpart E). Ensure your organization protects patient privacy rights and handles PHI appropriately.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Overview */}
      <section className="py-8 bg-[#F0F9FF]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="border-2 border-[#0EA5E9] mb-8">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-[#0F172A]">Overall Progress</h2>
                  <span className="text-3xl font-bold text-[#0EA5E9]">{overallProgress}%</span>
                </div>
                <div className="w-full bg-[#E2E8F0] rounded-full h-4 mb-4">
                  <div
                    className="bg-[#0EA5E9] h-4 rounded-full transition-all duration-300"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <p className="text-sm text-[#64748B] mb-6">
                  {checkedItems.size} of {privacyRuleItems.length} requirements completed
                </p>

                {/* Category Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((category) => {
                    const { checked, total, percentage } = getCategoryProgress(category)
                    const Icon = getCategoryIcon(category)
                    return (
                      <div key={category} className="bg-white rounded-lg p-4 border border-[#E2E8F0]">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-[#0EA5E9]/10 flex items-center justify-center">
                            <Icon className="h-5 w-5 text-[#0EA5E9]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0F172A] text-sm">{category}</h3>
                            <p className="text-xs text-[#64748B]">{checked}/{total}</p>
                          </div>
                        </div>
                        <div className="w-full bg-[#E2E8F0] rounded-full h-2">
                          <div
                            className="bg-[#0EA5E9] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            {/* April 2024 Update Notice */}
            <Card className="border-2 border-[#F59E0B] bg-[#FEF3C7] mb-8">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-6 w-6 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-[#92400E] mb-2">April 2024 Reproductive Health Privacy Rule</h3>
                    <p className="text-sm text-[#92400E]">
                      The Privacy Rule was updated in April 2024 to strengthen protections for reproductive healthcare information. This checklist includes the new requirements for restricting disclosures of PHI related to lawful reproductive health care.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Tab Buttons */}
            <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((category) => {
                const Icon = getCategoryIcon(category)
                const { checked, total } = getCategoryProgress(category)
                return (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                      activeCategory === category
                        ? "bg-[#0EA5E9] text-white"
                        : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {category} ({checked}/{total})
                  </button>
                )
              })}
            </div>

            {/* Checklist Items */}
            <Card className="border-2 border-[#E2E8F0]">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {Array.from(new Set(privacyRuleItems
                    .filter(item => item.category === activeCategory)
                    .map(item => item.subcategory)))
                    .map((subcategory) => {
                      const subcategoryItems = privacyRuleItems.filter(
                        item => item.category === activeCategory && item.subcategory === subcategory
                      )
                      return (
                        <div key={subcategory} className="border-l-4 border-[#0EA5E9] pl-4">
                          <h3 className="font-bold text-[#0F172A] mb-3 text-lg">{subcategory}</h3>
                          <div className="space-y-3">
                            {subcategoryItems.map((item) => (
                              <div
                                key={item.id}
                                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                  checkedItems.has(item.id)
                                    ? "bg-[#F0FDF4] border-[#86EFAC]"
                                    : "bg-white border-[#E2E8F0] hover:border-[#0EA5E9]"
                                }`}
                                onClick={() => toggleItem(item.id)}
                              >
                                <div className="flex items-start gap-3">
                                  {checkedItems.has(item.id) ? (
                                    <CheckCircle2 className="h-6 w-6 text-[#10B981] flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <Circle className="h-6 w-6 text-[#94A3B8] flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <h4 className={`font-bold mb-2 ${
                                      checkedItems.has(item.id) ? "text-[#15803D]" : "text-[#0F172A]"
                                    }`}>
                                      {item.text}
                                    </h4>
                                    <p className={`text-sm ${
                                      checkedItems.has(item.id) ? "text-[#15803D]" : "text-[#64748B]"
                                    }`}>
                                      {item.explanation}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#0EA5E9] to-[#0284C7]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Get Expert Help with Privacy Rule Compliance</h2>
            <p className="text-xl text-white/90 mb-8">
              Our HIPAA privacy experts can help you develop compliant policies, train your staff, and ensure you meet all Privacy Rule requirements including the latest 2024 updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/self-assessment">
                <Button size="lg" className="bg-white text-[#0EA5E9] hover:bg-white/90">
                  Take Privacy Assessment
                </Button>
              </a>
              <a href="/contact">
                <Button size="lg" className="bg-white/20 text-white border-2 border-white hover:bg-white hover:text-[#0EA5E9]">
                  Schedule Consultation
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
